// Rentals — the third kind of Realty work, beside a sale and a purchase.
//
// Grant, 2026-09-22: "we do help people find rentals from time to time.
// it is rare. but the OS needs to be able to handle this correctly."
//
// Until this module the OS had no rental record at all. BrokerMint did —
// its export carries a transaction type of "rent/lease" — and the importer
// kept that word as `sub_type = 'rent_lease'` on the property row while
// typing the record itself a Listing or a Home Purchase. So the rentals
// already in the database read as sales on the board, the list, the
// dashboard and the ADRE transaction log.
//
// ── THE SHAPE ─────────────────────────────────────────────────────────
//
// A rental keeps the two things every Realty surface already reads, and
// changes only the third:
//
//   * service_family — WHICH SIDE we represent, and so which property
//     table holds the row. Unchanged: a landlord rental is family
//     'listing' (realty_listings), a tenant rental is family
//     'home_purchase' (realty_purchases). About forty places key on the
//     family to pick a table, a person label or a checklist, and every
//     one of them keeps working.
//   * service_type — WHAT the product is. This is the new part:
//     'rental_listing' (we represent the landlord) and 'home_rental' (we
//     represent the tenant). Other companies have always used the type
//     this way (family 'policy', type 'home_policy'); Realty simply had
//     type == family until now.
//   * sub_type on the property row — BrokerMint's word, 'rent_lease',
//     written on every new rental too, so the Realty dashboard's
//     transaction-type mix counts old and new rentals the same way.
//
// DECISION: a type, not a flag column. A flag would be one more thing for
// every query to remember; the type is already what the record page, the
// list, the exports and the product-noun helpers read, so making it say
// "rental" makes all of them say it.
//
// ── THE MONEY ─────────────────────────────────────────────────────────
//
// A rental's fee is not a percentage of a sale price. Nothing in the OS
// may estimate one from the sale defaults (3.00% buy side, 1.98% listing
// side). Until a comp_rules row for a rental family exists, a rental's
// estimated revenue is unknown and says so; a CLOSED rental still records
// what BrokerMint (or the advisor) says it actually earned.
//
// Pure: no database, no I/O. Node-pool tested in tests/realty/rental.test.ts.

/** We represent the tenant: finding someone a home to rent. */
export const TENANT_RENTAL_SERVICE_TYPE = "home_rental";
/** We represent the landlord: leasing out an owner's home. */
export const LANDLORD_RENTAL_SERVICE_TYPE = "rental_listing";

export const RENTAL_SERVICE_TYPES: readonly string[] = [
  TENANT_RENTAL_SERVICE_TYPE,
  LANDLORD_RENTAL_SERVICE_TYPE,
];

/** BrokerMint's word for a rental, kept on the property row (migration 0028). */
export const RENTAL_SUB_TYPE = "rent_lease";

/** The Processing board a rental runs on. */
export const RENTAL_TRANSACTION_TYPE = "realty_rental";

/** Opportunity types for the two sides of a rental. */
export const TENANT_CONSULTATION = "tenant_consultation";
export const LANDLORD_CONSULTATION = "landlord_consultation";
export const RENTAL_OPPORTUNITY_TYPES: readonly string[] = [
  TENANT_CONSULTATION,
  LANDLORD_CONSULTATION,
];

export function isRentalServiceType(serviceType: string | null | undefined): boolean {
  return serviceType === TENANT_RENTAL_SERVICE_TYPE || serviceType === LANDLORD_RENTAL_SERVICE_TYPE;
}

/** The signals a caller may have on hand. Any one that says rental wins. */
export interface RentalSignals {
  serviceType?: string | null;
  /** realty_listings.sub_type / realty_purchases.sub_type */
  subType?: string | null;
  transactionType?: string | null;
  opportunityType?: string | null;
}

/**
 * Is this Realty record a rental?
 *
 * The sub_type arm is what makes an imported BrokerMint rental read
 * correctly before (or without) the retype pass — the property row has
 * always said "rent_lease" even while the record's type said "listing".
 */
export function isRealtyRental(signals: RentalSignals): boolean {
  if (isRentalServiceType(signals.serviceType)) return true;
  if (signals.subType === RENTAL_SUB_TYPE) return true;
  if (signals.transactionType === RENTAL_TRANSACTION_TYPE) return true;
  if (signals.opportunityType && RENTAL_OPPORTUNITY_TYPES.includes(signals.opportunityType)) {
    return true;
  }
  return false;
}

/**
 * The service shape to stamp on a new Realty record, from the side we
 * represent and whether it is a rental. The family never changes with
 * the kind — see THE SHAPE above.
 */
export function realtyServiceShape(
  family: "listing" | "home_purchase",
  rental: boolean,
): { service_type: string; service_family: "listing" | "home_purchase" } {
  if (!rental) return { service_type: family, service_family: family };
  return {
    service_type: family === "listing" ? LANDLORD_RENTAL_SERVICE_TYPE : TENANT_RENTAL_SERVICE_TYPE,
    service_family: family,
  };
}

/** The property row's sub_type for a new record: 'rent_lease' for a rental, else untouched. */
export function subTypeForNewRecord(rental: boolean): string | null {
  return rental ? RENTAL_SUB_TYPE : null;
}

/**
 * The retyped service_type for a record that should be a rental, given
 * its family. Used by the backfill pass. Null for a family that is not a
 * Realty side (defensive — the pass only reads Realty rows).
 */
export function rentalServiceTypeForFamily(family: string | null | undefined): string | null {
  if (family === "listing") return LANDLORD_RENTAL_SERVICE_TYPE;
  if (family === "home_purchase") return TENANT_RENTAL_SERVICE_TYPE;
  return null;
}

/**
 * The product noun for a rental, by side. "Rental Listing" when we
 * represent the landlord; "Home Rental" when we represent the tenant —
 * the same shape as Listing / Home Purchase on the sale side.
 */
export function rentalNoun(side: "buy" | "sell"): string {
  return side === "sell" ? "Rental Listing" : "Home Rental";
}

/** Who the people on a rental are, by side: the landlord or the tenant. */
export function rentalPartyLabel(side: "buy" | "sell"): string {
  return side === "sell" ? "Landlord" : "Tenant";
}

/**
 * Stages on the rental board where there is no lease start to set yet —
 * the home is not rented, or nobody has applied. The sale board's
 * equivalent is PRE_OFFER_STAGES in board-cards.ts.
 */
export const RENTAL_PRE_APPLICATION_STAGES: ReadonlySet<string> = new Set([
  "prelisting",
  "listed_for_rent",
]);

/** A monthly rent in whole dollars → "$2,150/mo". */
export function formatMonthlyRent(dollars: number | null | undefined): string | null {
  if (dollars === null || dollars === undefined || !Number.isFinite(dollars)) return null;
  return `${dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: dollars % 1 === 0 ? 0 : 2,
  })}/mo`;
}

// ── Lease terms: the three facts a lease has and a sale does not ──────
//
// Stored in realty_lease_terms, one row per rental property record
// (migration 20260922234122 says why it is its own table). Entered in
// whole or fractional DOLLARS like every other Realty money field; stored
// in cents.

export const LEASE_TERM_FIELDS = ["monthly_rent", "lease_start_date", "lease_end_date"] as const;
export type LeaseTermField = (typeof LEASE_TERM_FIELDS)[number];

export interface LeaseTermsPatch {
  monthly_rent_cents?: number | null;
  lease_start_date?: string | null;
  lease_end_date?: string | null;
}

/** Fat-finger ceiling for a monthly rent, not a business rule. */
export const MONTHLY_RENT_MAX_DOLLARS = 1_000_000;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Validate the lease-term keys PRESENT in a body. Absent keys stay
 * absent (a partial edit never clears an untouched value); a key present
 * with null / "" clears it. Returns the patch in storage units.
 */
export function parseLeaseTermsPatch(
  body: Record<string, unknown>,
): { ok: true; patch: LeaseTermsPatch } | { ok: false; error: string } {
  const patch: LeaseTermsPatch = {};
  if ("monthly_rent" in body) {
    const raw = body.monthly_rent;
    if (raw === null || raw === undefined || raw === "") {
      patch.monthly_rent_cents = null;
    } else {
      const n = typeof raw === "number" ? raw : Number(String(raw).replace(/[$,\s]/g, ""));
      if (!Number.isFinite(n) || n < 0 || n > MONTHLY_RENT_MAX_DOLLARS) {
        return { ok: false, error: "monthly_rent must be a dollar amount from $0 to $1,000,000, or null." };
      }
      patch.monthly_rent_cents = Math.round(n * 100);
    }
  }
  for (const key of ["lease_start_date", "lease_end_date"] as const) {
    if (!(key in body)) continue;
    const raw = body[key];
    if (raw === null || raw === undefined || raw === "") {
      patch[key] = null;
    } else if (typeof raw === "string" && ISO_DATE_RE.test(raw)) {
      patch[key] = raw;
    } else {
      return { ok: false, error: `${key} must be a YYYY-MM-DD date or null.` };
    }
  }
  return { ok: true, patch };
}

/** A lease that ends before it starts is a typo, not a lease. */
export function leaseDatesProblem(start: string | null, end: string | null): string | null {
  if (start && end && end < start) return "The lease can't end before it starts.";
  return null;
}

/**
 * The sale-only detail fields a rental refuses. A rent is not a price:
 * a number typed into sale_price or list_price is summed as sales volume
 * on the Realty dashboard, and a commission percentage is a sale
 * percentage (see THE MONEY above).
 */
export const SALE_ONLY_DETAIL_FIELDS: readonly string[] = [
  "sale_price",
  "list_price",
  "listing_side_pct",
  "buyer_broker_pct",
  "home_warranty",
  // A rental listing's asking figure is its rent, not a price, so it has
  // no original list PRICE either (2026-10-14).
  "original_list_price",
];
